// Admin mail service for bulk email functionality
const API_URL = process.env.REACT_APP_API_URL;

// Debug log for API_URL
console.log('adminMailService: API_URL loaded:', API_URL);
if (!API_URL) {
  console.error('adminMailService: REACT_APP_API_URL environment variable is not set!');
}

/**
 * Get Firebase ID token for API authentication
 * @returns {Promise<string>} - Firebase ID token
 */
const getAuthToken = async () => {
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('No authenticated user found');
  }

  return await user.getIdToken();
};

/**
 * Fetch users for admin bulk email with pagination and filtering
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Number of users per page
 * @param {string} params.lastUserId - Last user ID for cursor pagination
 * @param {string} params.subscriptionFilter - Filter by subscription status ('all', 'trial', 'active', 'expired')
 * @returns {Promise<Object>} - Users data with pagination info
 */
export const getUsersForBulkEmail = async (params = {}) => {
  try {
    const token = await getAuthToken();

    const queryParams = new URLSearchParams({
      limit: params.limit || 50,
      filter: params.subscriptionFilter || 'all'
    });

    // Add cursor if provided
    if (params.lastUserId) {
      queryParams.append('lastUserId', params.lastUserId);
    }

    // Add email search if provided
    if (params.emailSearch) {
      queryParams.append('emailSearch', params.emailSearch);
    }

    console.log('Fetching users for bulk email:', `${API_URL}/users?${queryParams}`);

    const response = await fetch(`${API_URL}/users?${queryParams}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `Failed to fetch users: ${response.statusText}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch users');
    }

    return result.data;
  } catch (error) {
    console.error('Error fetching users for bulk email:', error);
    throw error;
  }
};

/**
 * Send bulk email to selected users with campaign tracking
 * @param {Object} emailData - Email data
 * @param {string} emailData.campaignId - Campaign ID
 * @param {string[]} emailData.userIds - Array of user IDs to send to
 * @param {string} emailData.subject - Email subject
 * @param {string} emailData.htmlBody - Email HTML body
 * @returns {Promise<Object>} - Bulk email result
 */
export const getEmailConfig = async () => {
  try {
    const token = await getAuthToken();

    const response = await fetch(`${API_URL}/utility/email-config`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `Failed to fetch email config: ${response.statusText}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch email config');
    }

    return result.data;
  } catch (error) {
    console.error('Error fetching email config:', error);
    throw error;
  }
};

export const sendBulkEmail = async (emailData) => {
  try {
    const token = await getAuthToken();

    const { campaignId, userIds, subject, htmlBody } = emailData;

    // Validate required fields
    if (!campaignId || typeof campaignId !== 'string') {
      throw new Error('campaignId is required');
    }

    if (!Array.isArray(userIds) || userIds.length === 0) {
      throw new Error('userIds must be a non-empty array');
    }

    if (!subject || typeof subject !== 'string' || subject.trim().length === 0) {
      throw new Error('subject is required and must be a non-empty string');
    }

    if (!htmlBody || typeof htmlBody !== 'string' || htmlBody.trim().length === 0) {
      throw new Error('htmlBody is required and must be a non-empty string');
    }

    console.log('Sending bulk email to', userIds.length, 'users for campaign', campaignId);

    const response = await fetch(`${API_URL}/email/bulk`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        campaignId,
        userIds,
        subject: subject.trim(),
        htmlBody: htmlBody.trim()
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `Failed to send bulk email: ${response.statusText}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to send bulk email');
    }

    return result;
  } catch (error) {
    console.error('Error sending bulk email:', error);
    throw error;
  }
};

/**
 * Get email logs for admin monitoring
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Number of logs to fetch
 * @param {string} params.startAfter - Cursor for pagination
 * @param {string} params.userId - Filter by specific user ID
 * @returns {Promise<Object>} - Email logs data
 */
export const getEmailLogs = async (params = {}) => {
  try {
    const token = await getAuthToken();

    const queryParams = new URLSearchParams();
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.startAfter) queryParams.append('startAfter', params.startAfter);
    if (params.userId) queryParams.append('userId', params.userId);

    const response = await fetch(`${API_URL}/email/logs?${queryParams}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `Failed to fetch email logs: ${response.statusText}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch email logs');
    }

    return result.data;
  } catch (error) {
    console.error('Error fetching email logs:', error);
    throw error;
  }
};

/**
 * Get the currently active campaign
 * @returns {Promise<Object>} - Active campaign data with recipients
 */
export const getActiveCampaign = async () => {
  try {
    const token = await getAuthToken();

    const response = await fetch(`${API_URL}/campaigns/active`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `Failed to fetch active campaign: ${response.statusText}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch active campaign');
    }

    return result.data;
  } catch (error) {
    console.error('Error fetching active campaign:', error);
    throw error;
  }
};

/**
 * Get recipient statuses for specific users
 * @param {string[]} userIds - Array of user IDs
 * @returns {Promise<Object>} - Status map { userId: { status, email, sentAt, error } }
 */
export const getRecipientStatuses = async (userIds) => {
  try {
    const token = await getAuthToken();

    const response = await fetch(`${API_URL}/campaigns/recipient-statuses`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userIds }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `Failed to fetch recipient statuses: ${response.statusText}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch recipient statuses');
    }

    return result.data;
  } catch (error) {
    console.error('Error fetching recipient statuses:', error);
    throw error;
  }
};

/**
 * Reset campaign (complete current and create new)
 * @returns {Promise<Object>} - New campaign data
 */
export const resetCampaign = async () => {
  try {
    const token = await getAuthToken();

    const response = await fetch(`${API_URL}/campaigns/reset`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `Failed to reset campaign: ${response.statusText}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to reset campaign');
    }

    return result.data;
  } catch (error) {
    console.error('Error resetting campaign:', error);
    throw error;
  }
};

/**
 * Get user IDs by send status
 * @param {string} status - 'unsent', 'success', or 'fail'
 * @returns {Promise<string[]>} - Array of user IDs
 */
export const getUserIdsByStatus = async (status) => {
  try {
    const token = await getAuthToken();

    const response = await fetch(`${API_URL}/campaigns/users-by-status/${status}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
      throw new Error(errorData.message || `Failed to fetch users by status: ${response.statusText}`);
    }

    const result = await response.json();

    if (!result.success) {
      throw new Error(result.error || 'Failed to fetch users by status');
    }

    return result.data.userIds;
  } catch (error) {
    console.error('Error fetching users by status:', error);
    throw error;
  }
};